import {CompletionSource, Completion} from "@codemirror/autocomplete"
import {syntaxTree} from "@codemirror/language"
import {parser} from "@lezer/css"
import {LRLanguage, continuedIndent, indentNodeProp, foldNodeProp, foldInside, LanguageSupport} from "@codemirror/language"
import {styleTags, tags as t} from "@codemirror/highlight"
import {Extension} from "@codemirror/state"

let _properties: readonly Completion[] | null = null
const _additionalVariables: {type: string, label:string}[] = []

export function addCSSVariables(varnames:string[]) {
    _additionalVariables.splice(0,_additionalVariables.length);
    if(varnames) {
        varnames.forEach(name => {
            let newName = name;
            _additionalVariables.push({type: 'keyword', label: newName})
        })
    }
}

function jsStyleToCssProperty(name: string) {
    let array:string[] = [];
    for(let i=0,length=name.length;i<length;i++) {
        let ch = name.charAt(i);
        if(ch === ch.toUpperCase()) {
            array.push("-");
            array.push(ch.toLowerCase());
        } else {
            array.push(ch);
        }
    }
    return array.join("");
}

function properties() {
    if (!_properties && typeof document === "object" && document.body) {
        let names = []
        for (let prop in document.body.style) {
            prop = jsStyleToCssProperty(prop);
            if (!/[A-Z]|^-|^(item|length)$/.test(prop)) names.push(prop)
        }
        _properties = names.sort().map(name => ({type: "property", label: name}))
    }
    return _properties || []
}

const pseudoClasses = [
    "active", "after", "before", "checked", "default",
    "disabled", "empty", "enabled", "first-child", "first-letter",
    "first-line", "first-of-type", "focus", "hover", "in-range",
    "indeterminate", "invalid", "lang", "last-child", "last-of-type",
    "link", "not", "nth-child", "nth-last-child", "nth-last-of-type",
    "nth-of-type", "only-of-type", "only-child", "optional", "out-of-range",
    "placeholder", "read-only", "read-write", "required", "root",
    "selection", "target", "valid", "visited"
].map(name => ({type: "class", label: name}))

const values = [
    "var(--btcms-primary-color)",
    "var(--btcms-secondary-color)",
    "var(--btcms-background-color)",
    "var(--btcms-panel-primary-color)",
    "var(--btcms-panel-secondary-color)",
    "var(--btcms-font-family)",
    "above", "absolute", "activeborder", "additive", "activecaption", "after-white-space",
    "ahead", "alias", "all", "all-scroll", "alphabetic", "alternate", "always",
    "antialiased", "appworkspace", "asterisks", "attr", "auto", "auto-flow", "avoid", "avoid-column",
    "avoid-page", "avoid-region", "axis-pan", "background", "backwards", "baseline", "below",
    "bidi-override", "blink", "block", "block-axis", "bold", "bolder", "border", "border-box",
    "both", "bottom", "break", "break-all", "break-word", "bullets", "button", "button-bevel",
    "buttonface", "buttonhighlight", "buttonshadow", "buttontext", "calc", "capitalize",
    "caps-lock-indicator", "caption", "captiontext", "caret", "cell", "center", "checkbox", "circle",
    "cjk-decimal", "clear", "clip", "close-quote", "col-resize", "collapse", "color", "color-burn",
    "color-dodge", "column", "column-reverse", "compact", "condensed", "contain", "content",
    "contents", "content-box", "context-menu", "continuous", "copy", "counter", "counters", "cover",
    "crop", "cross", "crosshair", "currentcolor", "cursive", "cyclic", "darken", "dashed", "decimal",
    "decimal-leading-zero", "default", "default-button", "dense", "destination-atop", "destination-in",
    "destination-out", "destination-over", "difference", "disc", "discard", "disclosure-closed",
    "disclosure-open", "document", "dot-dash", "dot-dot-dash", "dotted", "double", "down", "e-resize",
    "ease", "ease-in", "ease-in-out", "ease-out", "element", "ellipse", "ellipsis", "embed", "end",
    "ethiopic-abegede-gez", "ethiopic-halehame-aa-er", "ethiopic-halehame-gez", "ew-resize", "exclusion",
    "expanded", "extends", "extra-condensed", "extra-expanded", "fantasy", "fast", "fill", "fill-box",
    "fixed", "flat", "flex", "flex-end", "flex-start", "footnotes", "forwards", "from",
    "geometricPrecision", "graytext", "grid", "groove", "hand", "hard-light", "help", "hidden", "hide",
    "higher", "highlight", "highlighttext", "horizontal", "hsl", "hsla", "hue", "icon", "ignore",
    "inactiveborder", "inactivecaption", "inactivecaptiontext", "infinite", "infobackground", "infotext",
    "inherit", "initial", "inline", "inline-axis", "inline-block", "inline-flex", "inline-grid",
    "inline-table", "inset", "inside", "intrinsic", "invert", "italic", "justify", "keep-all",
    "landscape", "large", "larger", "left", "level", "lighter", "lighten", "line-through", "linear",
    "linear-gradient", "lines", "list-item", "listbox", "listitem", "local", "logical", "loud", "lower",
    "lower-hexadecimal", "lower-latin", "lower-norwegian", "lowercase", "ltr", "luminosity", "manipulation",
    "match", "matrix", "matrix3d", "medium", "menu", "menutext", "message-box", "middle", "min-intrinsic",
    "mix", "monospace", "move", "multiple", "multiple_mask_images", "multiply", "n-resize", "narrower",
    "ne-resize", "nesw-resize", "no-close-quote", "no-drop", "no-open-quote", "no-repeat", "none",
    "normal", "not-allowed", "nowrap", "ns-resize", "numbers", "numeric", "nw-resize", "nwse-resize",
    "oblique", "opacity", "open-quote", "optimizeLegibility", "optimizeSpeed", "outset", "outside",
    "outside-shape", "overlay", "overline", "padding", "padding-box", "painted", "page", "paused",
    "perspective", "pinch-zoom", "plus-darker", "plus-lighter", "pointer", "polygon", "portrait",
    "pre", "pre-line", "pre-wrap", "preserve-3d", "progress", "push-button", "radial-gradient", "radio",
    "read-only", "read-write", "read-write-plaintext-only", "rectangle", "region", "relative", "repeat",
    "repeating-linear-gradient", "repeating-radial-gradient", "repeat-x", "repeat-y", "reset", "reverse",
    "rgb", "rgba", "ridge", "right", "rotate", "rotate3d", "rotateX", "rotateY", "rotateZ", "round",
    "row", "row-resize", "row-reverse", "rtl", "run-in", "running", "s-resize", "sans-serif", "saturation",
    "scale", "scale3d", "scaleX", "scaleY", "scaleZ", "screen", "scroll", "scrollbar", "scroll-position",
    "se-resize", "self-start", "self-end", "semi-condensed", "semi-expanded", "separate", "serif", "show",
    "single", "skew", "skewX", "skewY", "skip-white-space", "slide", "slider-horizontal",
    "slider-vertical", "sliderthumb-horizontal", "sliderthumb-vertical", "slow", "small", "small-caps",
    "small-caption", "smaller", "soft-light", "solid", "source-atop", "source-in", "source-out",
    "source-over", "space", "space-around", "space-between", "space-evenly", "spell-out", "square", "start",
    "static", "status-bar", "stretch", "stroke", "stroke-box", "sub", "subpixel-antialiased", "svg_masks",
    "super", "sw-resize", "symbolic", "symbols", "system-ui", "table", "table-caption", "table-cell",
    "table-column", "table-column-group", "table-footer-group", "table-header-group", "table-row",
    "table-row-group", "text", "text-bottom", "text-top", "textarea", "textfield", "thick", "thin",
    "threeddarkshadow", "threedface", "threedhighlight", "threedlightshadow", "threedshadow", "to", "top",
    "transform", "translate", "translate3d", "translateX", "translateY", "translateZ", "transparent",
    "ultra-condensed", "ultra-expanded", "underline", "unidirectional-pan", "unset", "up", "upper-latin",
    "uppercase", "url", "var()", "vertical", "vertical-text", "view-box", "visible", "visibleFill",
    "visiblePainted", "visibleStroke", "visual", "w-resize", "wait", "wave", "wider", "window", "windowframe",
    "windowtext", "words", "wrap", "wrap-reverse", "x-large", "x-small", "xor", "xx-large", "xx-small"
].map(name => ({type: "keyword", label: name})).concat([
    "aliceblue", "antiquewhite", "aqua", "aquamarine", "azure", "beige",
    "bisque", "black", "blanchedalmond", "blue", "blueviolet", "brown",
    "burlywood", "cadetblue", "chartreuse", "chocolate", "coral", "cornflowerblue",
    "cornsilk", "crimson", "cyan", "darkblue", "darkcyan", "darkgoldenrod",
    "darkgray", "darkgreen", "darkkhaki", "darkmagenta", "darkolivegreen",
    "darkorange", "darkorchid", "darkred", "darksalmon", "darkseagreen",
    "darkslateblue", "darkslategray", "darkturquoise", "darkviolet",
    "deeppink", "deepskyblue", "dimgray", "dodgerblue", "firebrick",
    "floralwhite", "forestgreen", "fuchsia", "gainsboro", "ghostwhite",
    "gold", "goldenrod", "gray", "grey", "green", "greenyellow", "honeydew",
    "hotpink", "indianred", "indigo", "ivory", "khaki", "lavender",
    "lavenderblush", "lawngreen", "lemonchiffon", "lightblue", "lightcoral",
    "lightcyan", "lightgoldenrodyellow", "lightgray", "lightgreen", "lightpink",
    "lightsalmon", "lightseagreen", "lightskyblue", "lightslategray",
    "lightsteelblue", "lightyellow", "lime", "limegreen", "linen", "magenta",
    "maroon", "mediumaquamarine", "mediumblue", "mediumorchid", "mediumpurple",
    "mediumseagreen", "mediumslateblue", "mediumspringgreen", "mediumturquoise",
    "mediumvioletred", "midnightblue", "mintcream", "mistyrose", "moccasin",
    "navajowhite", "navy", "oldlace", "olive", "olivedrab", "orange", "orangered",
    "orchid", "palegoldenrod", "palegreen", "paleturquoise", "palevioletred",
    "papayawhip", "peachpuff", "peru", "pink", "plum", "powderblue",
    "purple", "rebeccapurple", "red", "rosybrown", "royalblue", "saddlebrown",
    "salmon", "sandybrown", "seagreen", "seashell", "sienna", "silver", "skyblue",
    "slateblue", "slategray", "snow", "springgreen", "steelblue", "tan",
    "teal", "thistle", "tomato", "turquoise", "violet", "wheat", "white",
    "whitesmoke", "yellow", "yellowgreen"
].map(name => ({type: "constant", label: name})))

const tags = [
    "a", "abbr", "address", "article", "aside", "b", "bdi", "bdo", "blockquote", "body",
    "br", "button", "canvas", "caption", "cite", "code", "col", "colgroup", "dd", "del",
    "details", "dfn", "dialog", "div", "dl", "dt", "em", "figcaption", "figure", "footer",
    "form", "header", "hgroup", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "html", "i", "iframe",
    "img", "input", "ins", "kbd", "label", "legend", "li", "main", "meter", "nav", "ol", "output",
    "p", "pre", "ruby", "section", "select", "small", "source", "span", "strong", "sub", "summary",
    "sup", "table", "tbody", "td", "template", "textarea", "tfoot", "th", "thead", "tr", "u", "ul","current"
].map(name => ({type: "type", label: name}))

const span = /^[\w-]*/

/// CSS property and value keyword completion source.
export const cssCompletionSource: CompletionSource = context => {
    let {state, pos} = context, node = syntaxTree(state).resolveInner(pos, -1)
    if (node.name === "PropertyName")
        return {from: node.from, options: properties(), span}
    if (node.name === "ValueName") {
        return {from: node.from, options: [...values,..._additionalVariables], span}
    }
    if (node.name === "PseudoClassName")
        return {from: node.from, options: pseudoClasses, span}
    if (node.name === "TagName") {
        for (let {parent} = node; parent; parent = parent.parent)
            if (parent.name === "Block") return {from: node.from, options: properties(), span}
        return {from: node.from, options: tags, span}
    }

    if (!context.explicit) return null

    let above = node.resolve(pos), before = above.childBefore(pos)
    if (before && before.name === ":" && above.name === "PseudoClassSelector")
        return {from: pos, options: pseudoClasses, span}
    if (before && before.name === ":" && (above.name === "Declaration" || above.name === "ArgList")) {
        return {from: pos, options: values, span}
    }
    if (above.name === "Block")
        return {from: pos, options: properties(), span}

    return null
}

/// A language provider based on the [Lezer CSS
/// parser](https://github.com/lezer-parser/css), extended with
/// highlighting and indentation information.
export const cssLanguage = LRLanguage.define({
    parser: parser.configure({
        props: [
            indentNodeProp.add({
                Declaration: continuedIndent()
            }),
            foldNodeProp.add({
                Block: foldInside
            }),
            styleTags({
                "import charset namespace keyframes": t.definitionKeyword,
                "media supports": t.controlKeyword,
                "from to selector": t.keyword,
                NamespaceName: t.namespace,
                KeyframeName: t.labelName,
                TagName: t.tagName,
                ClassName: t.className,
                PseudoClassName: t.constant(t.className),
                IdName: t.labelName,
                "FeatureName PropertyName": t.propertyName,
                AttributeName: t.attributeName,
                NumberLiteral: t.number,
                KeywordQuery: t.keyword,
                UnaryQueryOp: t.operatorKeyword,
                "CallTag ValueName": t.atom,
                VariableName: t.variableName,
                Callee: t.operatorKeyword,
                Unit: t.unit,
                "UniversalSelector NestingSelector": t.definitionOperator,
                AtKeyword: t.keyword,
                MatchOp: t.compareOperator,
                "ChildOp SiblingOp, LogicOp": t.logicOperator,
                BinOp: t.arithmeticOperator,
                Important: t.modifier,
                Comment: t.blockComment,
                ParenthesizedContent: t.special(t.name),
                ColorLiteral: t.color,
                StringLiteral: t.string,
                ":": t.punctuation,
                "PseudoOp #": t.derefOperator,
                "; ,": t.separator,
                "( )": t.paren,
                "[ ]": t.squareBracket,
                "{ }": t.brace
            })
        ]
    }),
    languageData: {
        commentTokens: {block: {open: "/*", close: "*/"}},
        indentOnInput: /^\s*\}$/,
        wordChars: ""
    }
})

// FIXME remove on next major version
export const cssCompletion: Extension = cssLanguage.data.of({autocomplete: cssCompletionSource})

/// Language support for CSS.
export function css() {
    return new LanguageSupport(cssLanguage, cssCompletion)
}